import { useEffect, useState } from "react";
import { useLocation as useRouterLocation, useNavigate } from "react-router-dom";
import { Rocket } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSetupTasks, useSnoozeSetupTask } from "@/hooks/useSetupTasks";
import { useSetupWizard } from "@/contexts/SetupWizardContext";

/**
 * Popup flotante del asistente de puesta en marcha (19-sep-2026, petición de
 * Luis): recuerda -sin regañar- a los negocios que se registraron y dejaron
 * su perfil a medias (sin dirección, sin foto, sin página web...). El
 * mensaje entero (título/texto/CTA) lo manda el backend tal cual, sin
 * reescribirlo aquí. Solo se enseña `siguiente` (una tarea, la que decide el
 * backend) — nunca dos a la vez.
 *
 * SITIO ELEGIDO: centrado abajo, POR ENCIMA de la fila de las dos bolas de
 * esquina (WhatsApp/Conversaciones y Ayuda — ver `FloatingConversationsButton
 * .tsx`/`FloatingHelpButton.tsx`, que ya se reparten izquierda/derecha entre
 * ellas según el negocio). Un popup de esquina no vale aquí: la esquina
 * derecha SIEMPRE está ocupada (por la bola verde o, en su defecto, por la de
 * ayuda) y la izquierda lo está también en cuanto hay WhatsApp IA — no hay
 * hueco libre fijo en ninguna esquina en todos los estados. Centrado y con
 * margen vertical de sobra sobre la altura de las bolas (incluida la versión
 * móvil, más alta por el `bottom-10` de las bolas) evita pisarlas en
 * cualquier combinación.
 */
const HIDDEN_PATHS = ["/conversations", "/admin/soporte"];
/** Deja pasar unos segundos tras cargar para no saltar nada más entrar. */
const SHOW_DELAY_MS = 6000;
const SEEN_STORAGE_KEY = "rm_setup_popup_seen_ids";

function loadSeenIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SEEN_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function persistSeenIds(ids: Set<string>) {
  try {
    sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // Modo privado / storage lleno: no es crítico, como mucho reaparece en
    // otra navegación de la misma sesión.
  }
}

export default function SetupAssistantPopup() {
  const { user } = useAuth();
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const { data } = useSetupTasks(isAdmin);
  const snooze = useSnoozeSetupTask();
  // UN AVISO A LA VEZ: mientras al negocio le falte algo de la configuración
  // básica, manda el asistente guiado (tarjeta del panel + ventana paso a
  // paso). Este flotante sugiere cosas menores —la foto, la página web— y
  // enseñarlas encima del "tus clientes no pueden pedir cita" despistaba.
  const { pendiente: configuracionPendiente } = useSetupWizard();

  const [seenIds, setSeenIds] = useState<Set<string>>(() => loadSeenIds());
  const [delayElapsed, setDelayElapsed] = useState(false);

  const task = data?.siguiente ?? null;

  // Un temporizador nuevo por cada tarea distinta que llegue como
  // `siguiente` (no se resetea al navegar entre páginas: este componente
  // vive montado en el Layout durante toda la sesión).
  useEffect(() => {
    setDelayElapsed(false);
    if (!task) return;
    const timer = setTimeout(() => setDelayElapsed(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [task?.id]);

  const markSeen = (id: string) => {
    setSeenIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistSeenIds(next);
      return next;
    });
  };

  const onCtaRoute =
    !!task &&
    (routerLocation.pathname === task.ctaRuta ||
      routerLocation.pathname.startsWith(`${task.ctaRuta}/`));

  const shouldShow =
    isAdmin &&
    !configuracionPendiente &&
    !!task &&
    delayElapsed &&
    !seenIds.has(task.id) &&
    !onCtaRoute &&
    !HIDDEN_PATHS.some((p) => routerLocation.pathname.startsWith(p));

  // Cerrar con Escape (accesibilidad): NO cuenta como "Más tarde" — no suma
  // al contador de aplazamientos del backend, solo deja de insistir en lo
  // que queda de esta sesión de navegador. Sin trampa de foco: el popup no
  // es modal, Escape funciona esté el foco donde esté.
  useEffect(() => {
    if (!shouldShow || !task) return;
    const taskId = task.id;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") markSeen(taskId);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, task?.id]);

  if (!shouldShow || !task) return null;

  const handleNow = () => {
    markSeen(task.id);
    navigate(task.ctaRuta);
  };

  const handleLater = () => {
    // Optimista: se cierra siempre, en primer plano o en segundo plano se
    // reintentará otra sesión si la llamada falla (ver useSnoozeSetupTask).
    markSeen(task.id);
    snooze.mutate(task.id);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="setup-assistant-title"
      aria-describedby="setup-assistant-message"
      className="fixed z-40 inset-x-4 bottom-28 sm:inset-x-auto sm:bottom-24 sm:left-1/2 sm:w-[26rem] sm:-translate-x-1/2 animate-slide-up"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 bg-gradient-to-r from-primary-600 to-indigo-600 px-4 py-2.5 text-white">
          <Rocket className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <p className="text-xs font-semibold uppercase tracking-wide">
            Puesta en marcha
          </p>
        </div>
        <div className="p-4">
          <h3
            id="setup-assistant-title"
            className="font-semibold text-gray-900 mb-1"
          >
            {task.titulo}
          </h3>
          <p
            id="setup-assistant-message"
            className="text-sm text-gray-600 mb-4 leading-relaxed"
          >
            {task.mensaje}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleLater}
              className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Más tarde
            </button>
            <button
              type="button"
              onClick={handleNow}
              className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              Hacerlo ahora
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
